/**
 * The Feedback admin page's writes (`/api/v2/internal/feedback/*`, restServer
 * `apiv2/internal/feedback.py`, ADR 0042). The page reads reports and their comments straight from
 * Firestore, live, but changes them only through these, as the signed-in person: restServer's
 * feedback service keeps the lifecycle's rules for people and developers' agents alike, and
 * firestore.rules lets no client write a report. Internal: the web app's own, answered only for
 * this browser's registered web client device, and only for system admins (see `internalApi.ts`).
 *
 * Pure, so it stays Jest-testable: the page passes the credentials (`internalApiCredentials()`).
 */

import { InternalApiCredentials, internalApiHeaders, internalApiPath } from '@/utils/internalApi';

export const FEEDBACK_API_BASE_URL: string =
	process.env.VUE_APP_API_BASE_URL || 'https://api2.rundiffusion.com/api/v2';

export interface FeedbackApiFailure {
	ok: false;
	status: number;
	code: string; // the API's error code (FEEDBACK_REPORT_MOVED, FEEDBACK_REPORT_CLAIMED, ...), or NETWORK
	message: string; // the API's own words, safe to show
	details: Record<string, unknown> | null;
}

export type FeedbackApiResult<T> = { ok: true, value: T } | FeedbackApiFailure;

/** The failure, or null when it worked (`web` compiles without strictNullChecks, so `!result.ok` doesn't narrow). */
export function feedbackApiFailure<T> (result: FeedbackApiResult<T>): FeedbackApiFailure | null {
	return result.ok === true ? null : result;
}

export interface FeedbackApiRequest extends InternalApiCredentials {
	baseUrl: string;
	method: 'POST' | 'PATCH' | 'DELETE';
	// under /internal/feedback/, as `reports/12/status`
	path: string;
	// sent as JSON; or, with `files`, as the JSON in a multipart part named `part`
	body?: Record<string, unknown>;
	files?: File[];
	part?: string;
	fetchImpl?: typeof fetch;
}

async function failureFrom (response: Response): Promise<FeedbackApiFailure> {
	let body: { error?: { code?: string, message?: string, details?: Record<string, unknown> } } | null = null;
	try {
		body = await response.json();
	} catch (e) {
		// not JSON
	}
	return {
		ok: false,
		status: response.status,
		code: body?.error?.code || `HTTP_${response.status}`,
		message: body?.error?.message || 'Something went wrong. Try again.',
		details: body?.error?.details || null,
	};
}

const networkFailure = (): FeedbackApiFailure => ({
	ok: false, status: 0, code: 'NETWORK', message: 'Couldn\'t reach RunDiffusion. Check your connection and try again.', details: null,
});

/** One write. With files, the request is multipart: the body as JSON in `part`, each file in `shots`. */
export async function feedbackApiRequest<T> (input: FeedbackApiRequest): Promise<FeedbackApiResult<T>> {
	const url: string = `${input.baseUrl}${internalApiPath(`/feedback/${input.path.replace(/^\/+/, '')}`)}`;
	const headers: Record<string, string> = internalApiHeaders(input);
	let body: BodyInit | undefined;
	if (input.files?.length) {
		const form: FormData = new FormData();
		form.append(input.part || 'body', JSON.stringify(input.body || {}));
		input.files.forEach((file: File) => form.append('shots', file, file.name));
		body = form;
	} else if (input.body !== undefined) {
		headers['Content-Type'] = 'application/json';
		body = JSON.stringify(input.body);
	}
	try {
		const response: Response = await (input.fetchImpl || fetch)(url, { method: input.method, headers, body });
		if (!response.ok) return failureFrom(response);
		const value: T = response.status === 204 ? null as unknown as T : await response.json();
		return { ok: true, value };
	} catch (e) {
		return networkFailure();
	}
}
